import React from 'react';
import { Link } from 'react-router-dom';
import { FiActivity, FiGlobe, FiShield, FiTrendingUp, FiArrowUpRight } from 'react-icons/fi';
import Button from './ui/Button';
import Surface from './ui/Surface';
import styles from './HomePage.module.css';

const features = [
  {
    icon: <FiActivity />,
    title: 'Self-service execution',
    description: 'Quote, book, and track freight in seconds with a cohesive command center for teams and customers.'
  },
  {
    icon: <FiTrendingUp />,
    title: 'Intelligence in every workflow',
    description: 'Blend operational telemetry with predictive analytics to orchestrate better outcomes on every lane.'
  },
  {
    icon: <FiGlobe />,
    title: 'Network-wide visibility',
    description: 'Unify FTL and LTL data streams, documents, and communications without juggling multiple tabs.'
  },
  {
    icon: <FiShield />,
    title: 'Enterprise-grade guardrails',
    description: 'Role-based controls, audit trails, and secure integrations keep you compliant and trusted.'
  }
];

const stats = [
  { label: 'Average savings per load', value: '$142', caption: 'When executed through FreightLogic' },
  { label: 'Carrier adoption', value: '94%', caption: 'Vetted network with digital compliance' },
  { label: 'Booking speed', value: '3.2 min', caption: 'Average quote-to-book time' },
  { label: 'Visibility satisfaction', value: '98%', caption: 'Of operators cite real-time clarity' }
];

const HomePage = () => {
  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <span className={styles.tagline}>Premium logistics OS</span>
          <h1 className={styles.heroTitle}>Deliver premium experiences across every shipment lifecycle.</h1>
          <p className={styles.heroSubtitle}>
            FreightLogic is the unified operating system designed for ambitious brokerages and shippers that expect the polish of
            an enterprise suite without compromising speed.
          </p>
          <div className={styles.heroActions}>
            <Button as={Link} to="/login" variant="primary" size="lg">
              Enter your workspace
            </Button>
            <Button as={Link} to="/register" variant="ghost" size="lg">
              Talk to our team
            </Button>
          </div>
        </div>
        <div className={styles.heroMedia}>
          <div className={styles.heroCard}>
            <div className={styles.heroCardInner}>
              <div>
                <span className={styles.tagline}>Live command</span>
                <h3>Control tower mode</h3>
                <p>Monitor milestones, documents, and spend projections in a single glass dashboard.</p>
              </div>
              <Surface tone="glass" padding="md">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <div>
                    <strong style={{ fontSize: '2.2rem', letterSpacing: '-0.04em' }}>128</strong>
                    <p style={{ marginTop: '4px', fontSize: '0.85rem' }}>Active shipments synched</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 600, color: 'var(--color-success)' }}>
                      <FiArrowUpRight /> 12%
                    </span>
                    <p style={{ marginTop: '4px', fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>Week over week</p>
                  </div>
                </div>
              </Surface>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.gridSection}>
        <div className={styles.sectionHeader}>
          <div>
            <h2>Luxury-grade experiences, purpose-built for logistics</h2>
            <p>
              Harmonize your data, teams, and partners with a design system that feels as refined as the world-class brands you ship for.
            </p>
          </div>
          <Button as={Link} to="/login" variant="secondary">
            Explore the console
          </Button>
        </div>
        <div className={styles.featureGrid}>
          {features.map((feature) => (
            <div key={feature.title} className={styles.featureCard}>
              <div className={styles.featureIcon}>{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
        <div className={styles.statsStrip}>
          {stats.map((stat) => (
            <div key={stat.label} className={styles.statTile}>
              <span className={styles.statLabel}>{stat.label}</span>
              <span className={styles.statValue}>{stat.value}</span>
              <p>{stat.caption}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className={styles.footer}>
        <span>© {new Date().getFullYear()} FreightLogic. Crafted with precision.</span>
        <div className={styles.footerLinks}>
          <Link to="/privacy">Privacy</Link>
          <Link to="/security">Security</Link>
          <Link to="/accessibility">Accessibility</Link>
          <Link to="/contact">Contact</Link>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
